import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { RichTextEditorComponent } from '../../components/rich-text-editor/rich-text-editor.component';
import { SuggestContentComponent } from '../../components/suggest-content-dialog/suggest-content-dialog.component';
import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import { AfterBlockDragService } from './after-block-drag.service';

// Interface
export interface RichTextBlock {
  rawHtml?: string;
}

// Default blank value
export const blankRichTextBlock: RichTextBlock = {
  rawHtml: '', // can't use null or the editor breaks
};

@Component({
  selector: 'email-rich-text-block',
  templateUrl: './rich-text-block.component.html',
  styleUrls: ['./rich-text-block.component.scss'],
})
export class RichTextBlockComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'email-rich-text-block';
  @ViewChild('richTextEditor') richTextEditor: RichTextEditorComponent;

  @Input() content: RichTextBlock;
  @Input() contentGenerationEnabled: boolean;

  @Output() isBlockValid = new EventEmitter<boolean>();

  isValid$: Observable<boolean> = this.isBlockValid.asObservable();

  _rawHTML: string;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private emailPreview: EmailPreviewRefreshService,
    private afterBlockDrag: AfterBlockDragService,
    private zone: NgZone,
    private dialog: MatDialog,
    private posthogService: ProductAnalyticsService,
    private _: GalaxyAiIconService,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit(): void {
    this._rawHTML = this.content.rawHtml;

    // Update rich text editor after drag happens
    // otherwise content disappears
    this.subscriptions.push(
      this.afterBlockDrag.dragHappenedEmitter$.subscribe(() => {
        this.whileDragging();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  updateText(): void {
    this.zone.run(() => this.emailPreview.refresh());
  }

  updateStatus(event: ValidationErrors | null): void {
    this.isBlockValid.emit(!event);
  }

  public whileDragging(): void {
    this.richTextEditor?.reloadEditor();
  }

  async openSuggestAIContentDialog(): Promise<void> {
    this.posthogService.trackEvent('user-clicked-rich-text-generate-content', 'suggest-content', 'click', null);
    const dialogRef = this.dialog.open(SuggestContentComponent, {
      viewContainerRef: this.viewContainerRef,
      width: '600px',
      height: 'auto',
    });
    const content = await firstValueFrom(dialogRef.afterClosed());
    if (content) {
      this.richTextEditor.insertAtCursor(content);
    }
  }
}
